import { apiClient } from "@/api/api-client";
import { Tiptap } from "@/components/common/tiptap/Tiptap";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import TagsInput from "@/components/core/forms/tags-input";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { articleStatuses } from "@/constants/article-statuses";
import { type Article, type ArticleFormSchema, articleFormSchema } from "@/features/dashboard/types/article.ts";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle } from "@tabler/icons-react";
import { IconClockHour4, IconUserFilled } from "@tabler/icons-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import articleCategoryService from "../api/services/article-category-service";
import type { ArticleCategory } from "../types/article-category";
import ArticlePreviewModal from "./article-preview-modal";

export default function ArticleEditor({ data }: { data: Article | undefined }) {
	const categoryBadges = ["bg-orange-50 text-orange-600", "bg-blue-50 text-blue-600", "bg-purple-50 text-purple-600", "bg-cyan-50 text-cyan-600"];
	const statusColors = {
		Approved: "text-green-600",
		Rejected: "text-red-600",
		Pending: "text-yellow-600",
		Unpublished: "text-orange-600",
		Draft: "text-gray-600",
	};
	const { history } = useRouter();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const modal = useModal();
	const [image, setImage] = useState<string | null>(null);
	const { user } = useAuth();
	const form = useForm<ArticleFormSchema>({
		resolver: zodResolver(articleFormSchema),
		defaultValues: {
			id: data?.id ? data.id : null,
			title: data?.title ? data.title : "",
			body: data?.body ? data.body : "",
			status: "Draft",
			categories: data?.categories ? data?.categories : [],
			coverphoto: data?.coverphoto ? data?.coverphoto : undefined,
			caption: data?.caption ? data?.caption : "",
			author: data?.author ? data?.author : user?.full_name,
			created_at: data?.created_at ? data?.created_at : "",
		},
	});

	const onSubmit = async (data: ArticleFormSchema) => {
		const formData = new FormData();
		for (const key of Object.keys(data)) {
			const typedKey = key as keyof ArticleFormSchema;
			const value = data[typedKey]; // Store value in a variable for better type inference

			if (typedKey === "categories") {
				formData.append("categories", JSON.stringify(value as unknown));
			} else if (value instanceof File) {
				formData.append(key, value);
			} else if (typeof value === "string") {
				formData.append(key, value);
			}
		}

		if (data.id) {
			try {
				setIsLoading(true);
				const response = await apiClient.post(`/articles/${data.id}`, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});

				console.log(response);

				toast.success("Article successfully updated.");

				navigate({
					to: `/articles/${data.id}`,
					reloadDocument: true,
				});
				// window.location.href = "/articles";
			} catch (error) {
				toast.error("Failed to update article.");
			}
			setIsLoading(false);
		} else {
			try {
				setIsLoading(true);
				const response = await apiClient.post("/articles", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});

				console.log(response);

				toast.success("Article successfully saved.");

				// navigate({
				//   to: "/articles",
				// });
				window.location.href = "/articles";
			} catch (error) {
				toast.error("Failed to save article.");
			}

			setIsLoading(false);
		}
	};

	const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files?.[0] || null;
			setImage(URL.createObjectURL(file));
			if (file) {
				form.setValue("coverphoto", file);
			}
			// form.setValue('cover_photo', file);
		}
	};

	const openPreviewModal = (article: Article) => {
		console.log("article", article);
		modal.openModal({
			allowClose: true,
			title: "Article Preview",
			size: "xl",
			showHeader: false,
			showFooter: false,
			component: <ArticlePreviewModal data={article} />,
		});
	};

	const loadOptions = async (value: string) => {
		const items = await articleCategoryService.list({
			"filter[search]": value,
		});

		return items.data.map((item: ArticleCategory) => ({
			value: item.id,
			label: item.name,
		}));
	};

	const handleSetFile = (file) => {
		setImage(file ? URL.createObjectURL(file) : null);
	};

	const handleRemoveUploadedFile = () => {
		setImage(null);
		form.setValue("coverphoto", undefined);
		//add code for removing media
	};

	useEffect(() => {
		if (data?.coverphoto?.original_url) {
			setImage(data.coverphoto.original_url);
		}
	}, [data?.coverphoto?.original_url]);

	return (
		<Form {...form}>
			<form
				className="flex flex-col w-full max-w-base gap-6"
				onSubmit={(e) => e.preventDefault()}
				// onSubmit={form.handleSubmit(onSubmit)}
			>
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Article Editor</span>
						<span className="text-base text-foreground/70">Create article fast, easy</span>
					</div>
					<div className="inline-flex flex-row w-auto gap-2">
						<Button
							type="submit"
							variant="ghost"
							disabled={isLoading}
							loading={isLoading}
							onClick={() => {
								window.location.href = `/articles?status=${data?.status ? data?.status : "Pending"}`;
							}}
						>
							Back
						</Button>

						{!data?.status || data?.status === articleStatuses.DRAFT ? (
							<Button
								variant="ghost"
								disabled={isLoading}
								loading={isLoading}
								onClick={() => {
									form.setValue("status", "Draft");
									// form.handleSubmit((data) => onSubmit(data, form.reset));
									form.trigger();
									// form.handleSubmit(onSubmit)
									onSubmit(form.getValues());
								}}
							>
								Draft
							</Button>
						) : null}
						<Button
							variant="default"
							disabled={isLoading}
							loading={isLoading}
							onClick={() => {
								form.setValue("status", "Pending");
								// form.handleSubmit(onSubmit);
								// onSubmit(form.getValues());
								form.trigger();
								// form.handleSubmit(onSubmit)

								onSubmit(form.getValues());
							}}
						>
							{data?.status === articleStatuses.UNPUBLISHED ? "Republish" : "Submit"}
						</Button>
					</div>
				</div>
				<div className="flex flex-col w-full">
					<Tabs defaultValue="editor" onValueChange={() => form.trigger()}>
						<TabsList>
							<TabsTrigger value="editor">Editor</TabsTrigger>
							<TabsTrigger value="preview">Preview</TabsTrigger>
							<TabsTrigger value="logs">Logs</TabsTrigger>
						</TabsList>

						<TabsContent value="editor">
							<div className="my-4">
								{data?.status === articleStatuses.REJECTED || data?.status === articleStatuses.UNPUBLISHED ? (
									<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
										<div className="col-span-12 w-full">
											<Alert
												type="error"
												allowClose={false}
												title={data.status === articleStatuses.REJECTED ? "Article Rejected" : "Article Unpublished"}
												className=""
												icon={<IconAlertCircle size={20} stroke={2} />}
											>
												<div className="flex flex-col w-full">
													<span className="">{data.latest_log?.remarks}</span>
												</div>
											</Alert>
										</div>
									</div>
								) : (
									<></>
								)}

								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
									<div className="col-span-12 w-full">
										<FormField
											control={form.control}
											name="title"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Title</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input type="text" autoComplete="off" {...field} disabled={isLoading} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>

								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
									<div className="col-span-12 w-full">
										<FormField
											control={form.control}
											name="categories"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Categories</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<TagsInput
															isMulti={true}
															cacheOptions
															defaultOptions
															defaultValue={field.value}
															onValueChange={field.onChange}
															loadOptions={loadOptions}
															disabled={isLoading}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>

								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
									<div className="col-span-12 w-full">
										<FormField
											control={form.control}
											name="cover_photo"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Cover Photo</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<InputFile
															{...field}
															accept="image/png, image/jpeg"
															onChange={onImageChange}
															setFile={handleSetFile}
															removeUploadedFile={handleRemoveUploadedFile}
															uploadedFile={data?.coverphoto || null}
															disabled={isLoading}
														/>
													</FormGroup>
													{image && (
														<div className="flex align-middle justify-center my-6 border">
															<img src={image} className="w-6/12" alt="Article Cover" />
														</div>
													)}
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>

								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
									<div className="col-span-12 w-full">
										<FormField
											control={form.control}
											name="caption"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Photo Caption</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input type="text" autoComplete="off" disabled={isLoading} {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>

								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-12 w-full">
										<FormField
											control={form.control}
											name="body"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Body</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														{/* <Input type="text" autoComplete="off" {...field} /> */}
														<Tiptap content={field.value} onUpdate={field.onChange} disabled={true} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							</div>
						</TabsContent>

						<TabsContent value="preview">
							<div className="w-full h-full flex flex-col gap-6 mt-10">
								<div className="flex justify-center">
									<div className="w-9/12 gap-3">
										<div className="flex flex-col gap-2">
											<div className="text-3xl font-bold">{form.getValues("title")}</div>

											<div className="flex flex-row gap-4 items-center">
												<div className="flex flex-row gap-1 items-center text-gray-500">
													<IconUserFilled stroke={2} size={18} />
													<div className="font-normal text-base">{user?.full_name}</div>
												</div>
												<div className="flex flex-row gap-1 items-center text-gray-500">
													<IconClockHour4 stroke={2} size={18} />
													<div className="font-normal text-base">
														Published {data?.updated_at ? formatDate(data.updated_at) : new Date(Date.now()).toLocaleString()}
													</div>
												</div>
											</div>

											<div className="flex flex-row gap-2 mt-5 mb-4">
												{data?.categories?.map((category, catIndex) => (
													<div
														className={`inline-block px-2 py-1 font-medium text-xs self-start ${categoryBadges[catIndex] || "bg-gray-100 text-gray-500"}`}
														key={catIndex}
													>
														{category.label}
													</div>
												))}
											</div>
										</div>

										<div className="flex flex-col gap-3">
											<div className="bg-gray-300 h-full w-full">
												{image && <img className="h-full w-full object-cover" src={image} alt="Article Cover" />}
												{/* {data?.coverphoto &&
                        !data.coverphoto.collection_name ? (
                          <img
                            className="h-full w-full object-cover"
                            src={URL.createObjectURL(data.coverphoto)}
                          />
                        ) : (
                          <></>
                        )}
                        {data?.coverphoto &&
                        "original_url" in data?.coverphoto ? (
                          <img
                            className="h-full w-full object-cover"
                            src={data.coverphoto.original_url as string}
                          />
                        ) : (
                          <></>
                        )} */}
											</div>
											<div className="text-base text-gray-500 italic text-center mb-4">{form.getValues("caption")}</div>
										</div>

										<div className="font-base text-gray-600 text-pretty">
											<span
												className="text-base text-foreground-dark"
												// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
												dangerouslySetInnerHTML={{
													__html: form.getValues("body") || "",
												}}
												key={Date()}
											/>
										</div>
									</div>
								</div>
							</div>
						</TabsContent>

						<TabsContent value="logs">
							<div className="my-4">
								{data?.logs?.map((log, index) => (
									<div key={index} className="flex flex-row items-center w-full rounded-md gap-2 p-2">
										<div className="flex flex-col w-full">
											<span className="text-sm text-foreground/70">{formatDate(log.created_at)}</span>
											<span className={clsx("text-sm font-bold", statusColors[log.status] || "text-gray-500")}>{log.status}</span>
											<span className="text-sm">{log.remarks}</span>
										</div>
									</div>
								))}
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</form>
		</Form>
	);
}
